import { useState } from "react";
import { ActionDialog, Button, Tags } from "../../ui";
import { approveOnBehalfAPI, cancelCampaignAPI, changeCampaignStatusAPI, decideCampaignAPI, deleteCampaignRequestAPI } from "../../../utils/api/actions";
import EditCampaignModal from "./EditCampaignModal";
import "./ActionWorkflowButtons.css";

// The buttons of ONE campaign action (Operations page, its Campaign Actions and the
// Dashboard Requests rows). They show exactly what the server says the viewer may
// do now (`allowed`, from services/campaignWorkflow.js - the single definition of the
// workflow), so Operations and Dashboard cannot disagree and no rule is repeated here.
//
// Every action opens a confirmation first; rejecting needs a reason; the dialog locks
// while the call runs (no double submit) and keeps the server's message on failure.
//
//   Step 1  Store review   Store: Approve, Reject          LUD: Approve on behalf (am, dmm), Delete
//   Step 2  LUD review     Store: waiting                  LUD: Approve, Reject
//   Step 3  In progress    Store: waiting                  LUD: Activate, Cancel
//   Step 4  Activated      Store: waiting                  LUD: Complete
//   Update: steps 1 and 2 only.

const TEXT = {
  en: {
    approve: "Approve", reject: "Reject", behalf: "Approve on behalf of client", activate: "Activate", cancel: "Cancel", complete: "Complete", delete: "Delete", update: "Update",
    waiting: "Waiting", waitingTitle: "Waiting for the next step - no action is needed from you",
    confirmApprove: "Confirm approval", confirmReject: "Confirm rejection", confirmDelete: "Confirm deletion", confirmActivate: "Confirm activation", confirmCancel: "Confirm cancellation", confirmComplete: "Confirm completion",
    titles: { approve: "Approve this request", reject: "Reject this request", behalf: "Approve on behalf of client", delete: "Delete this request", activate: "Activate this campaign", cancel: "Cancel this request", complete: "Complete this campaign" },
    texts: {
      approve: "Your approval is recorded and the request moves to the next step.",
      reject: "A rejection is final. Write the reason; it stays on the request.",
      behalf: "Your approval is recorded under your own name, role and reason. The independent LUD manager approval is still required afterwards, from a different person.",
      delete: "The request moves to the deleted campaigns; its history is kept.",
      activate: "The campaign becomes active.",
      cancel: "The request is canceled and its history is kept.",
      complete: "The campaign is marked as completed. This is final.",
    },
    rejectReason: "Rejection reason (required)", behalfReason: "Reason (required)", behalfAck: "I confirm the client has agreed to this request", cancelReason: "Cancellation reason (optional)",
    dialog: { confirm: "Confirm", cancelDialog: "Go back", reasonRequired: "A reason is required.", failed: "The action could not be saved." },
  },
  ar: {
    approve: "موافقة", reject: "رفض", behalf: "اعتماد نيابةً عن العميل", activate: "تفعيل", cancel: "إلغاء", complete: "إكمال", delete: "حذف", update: "تعديل",
    waiting: "بانتظار الإجراء", waitingTitle: "بانتظار الخطوة التالية - لا يلزمك أي إجراء",
    confirmApprove: "تأكيد الموافقة", confirmReject: "تأكيد الرفض", confirmDelete: "تأكيد الحذف", confirmActivate: "تأكيد التفعيل", confirmCancel: "تأكيد الإلغاء", confirmComplete: "تأكيد الإكمال",
    titles: { approve: "الموافقة على الطلب", reject: "رفض الطلب", behalf: "اعتماد نيابةً عن العميل", delete: "حذف الطلب", activate: "تفعيل الحملة", cancel: "إلغاء الطلب", complete: "إكمال الحملة" },
    texts: {
      approve: "سيُسجَّل اعتمادك وينتقل الطلب إلى الخطوة التالية.",
      reject: "الرفض نهائي. اكتب السبب؛ سيبقى مسجلاً على الطلب.",
      behalf: "سيُسجَّل اعتمادك باسمك ودورك وسببك. تبقى موافقة مدير LUD المستقلة مطلوبة بعد ذلك من شخص آخر.",
      delete: "سينتقل الطلب إلى الحملات المحذوفة مع الاحتفاظ بسجله.",
      activate: "ستصبح الحملة فعّالة.",
      cancel: "سيُلغى الطلب مع الاحتفاظ بسجله.",
      complete: "ستُسجَّل الحملة كمكتملة. هذا الإجراء نهائي.",
    },
    rejectReason: "سبب الرفض (إلزامي)", behalfReason: "السبب (إلزامي)", behalfAck: "أؤكد أن العميل وافق على هذا الطلب", cancelReason: "سبب الإلغاء (اختياري)",
    dialog: { confirm: "تأكيد", cancelDialog: "تراجع", reasonRequired: "السبب مطلوب.", failed: "تعذّر تنفيذ الإجراء." },
  },
};

const token = () => localStorage.getItem("auth-token");

// `allowed`  the server's flags for this action and viewer
// `action`   the action itself (needed by Update); or `loadAction()` to fetch it when Update is clicked
// `onDone`   called after every successful action (refresh the list)
// `empty`    shown instead of a "waiting" label when nothing can be done (optional)
export default function ActionWorkflowButtons({ id, allowed, action, loadAction, lang = "en", onDone, size = "sm", empty = null }) {
  const t = TEXT[lang === "ar" ? "ar" : "en"];
  const [dialog, setDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  if (!allowed || !allowed.stage) return null;

  const close = () => setDialog(null);
  const done = async (result) => {
    close();
    await onDone?.(result);
  };
  const run = (call) => async (reason) => {
    if (!token()) throw new Error(t.dialog.failed);
    await done(await call(token(), reason));
  };

  const approve = allowed.approveAsClient || allowed.approveAsManager;
  const reject = allowed.rejectAsClient || allowed.rejectAsManager;
  const buttons = [];
  if (approve) buttons.push({ key: "approve", label: t.approve, variant: "success", appearance: "filled" });
  if (reject) buttons.push({ key: "reject", label: t.reject, variant: "danger", appearance: "soft" });
  if (allowed.approveOnBehalf) buttons.push({ key: "behalf", label: t.behalf, variant: "neutral", appearance: "soft" });
  if (allowed.activate) buttons.push({ key: "activate", label: t.activate, variant: "primary", appearance: "filled" });
  if (allowed.complete) buttons.push({ key: "complete", label: t.complete, variant: "primary", appearance: "filled" });
  if (allowed.cancel) buttons.push({ key: "cancel", label: t.cancel, variant: "danger", appearance: "soft" });
  if (allowed.delete) buttons.push({ key: "delete", label: t.delete, variant: "danger", appearance: "soft" });
  const deciding = buttons.length > 0;
  if (allowed.stage >= 5) return null; // completed: nothing to do or wait for
  if (allowed.edit) buttons.push({ key: "update", label: t.update, variant: "neutral", appearance: "soft" });

  const open = async (key) => {
    if (key !== "update") {
      setDialog(key);
      return;
    }
    if (action) {
      setEditing(action);
      return;
    }
    setLoading(true);
    try {
      setEditing(await loadAction());
    } finally {
      setLoading(false);
    }
  };

  const dialogs = {
    approve: { confirm: t.confirmApprove, run: run((tk) => decideCampaignAPI(tk, id, true)) },
    reject: { confirm: t.confirmReject, danger: true, reason: { label: t.rejectReason, required: true, min: 1 }, run: run((tk, reason) => decideCampaignAPI(tk, id, false, reason)) },
    behalf: { confirm: t.confirmApprove, reason: { label: t.behalfReason, required: true }, ack: t.behalfAck, run: run((tk, reason) => approveOnBehalfAPI(tk, id, reason)) },
    delete: { confirm: t.confirmDelete, danger: true, run: run((tk) => deleteCampaignRequestAPI(tk, id)) },
    activate: { confirm: t.confirmActivate, run: run((tk) => changeCampaignStatusAPI(tk, id, "active")) },
    cancel: { confirm: t.confirmCancel, danger: true, reason: { label: t.cancelReason, required: false }, run: run((tk, reason) => cancelCampaignAPI(tk, id, reason || undefined)) },
    complete: { confirm: t.confirmComplete, run: run((tk) => changeCampaignStatusAPI(tk, id, "completed")) },
  };
  const current = dialog ? dialogs[dialog] : null;

  return (
    <span className="ca-wf" role="group">
      {buttons.map((button) => (
        <Button key={button.key} size={size} variant={button.variant} appearance={button.appearance} loading={button.key === "update" && loading} disabled={loading} onClick={() => open(button.key)}>
          {button.label}
        </Button>
      ))}
      {/* the other side of the workflow: nothing to do now */}
      {!deciding && allowed.stage < 5 && (
        empty || (
          <Tags size="sm" variant="info" appearance="soft" className="ca-wf-wait" title={t.waitingTitle}>
            {t.waiting}
          </Tags>
        )
      )}
      {current && (
        <ActionDialog labels={t.dialog} title={t.titles[dialog]} description={t.texts[dialog]} reason={current.reason} ack={current.ack} confirmLabel={current.confirm} danger={current.danger} onClose={close} onConfirm={current.run} />
      )}
      {editing && (
        <EditCampaignModal
          action={editing}
          onClose={() => setEditing(null)}
          onSaved={async (saved) => {
            await onDone?.(saved);
          }}
        />
      )}
    </span>
  );
}
